import DyGraph from 'dygraphs';
import { IsotopicDistribution, getBestPeaks, MF } from 'mass-tools';
import React, { useEffect, useState, memo } from 'react';

import useChartSettings from '../../hooks/useChartSettings';
import interactionModel from '../../utils/interactionModel';

import barChartPlotter from './BarChartPlotter';

const bestPeakAnnotator = (g, data) => {
  const xRange = g.xAxisRange();

  let peaks = getBestPeaks(data, {
    from: xRange[0],
    to: xRange[1],
    limit: 5,
    numberSlots: 10,
    threshold: 0.01,
  }).filter((peak) => !peak.close);

  g.ready(() => {
    g.setAnnotations(
      peaks.map((peak) => ({
        series: 'Centroid',
        x: peak.x,
        tickColor: 'grey',
        shortText: `${peak.x.toFixed(4)}\n${peak.y.toFixed(2)}%`,
      })),
    );
  });
};

const IsotopicDistributionChart = memo(
  ({ id = 0, mf, option, style, settings }) => {
    // eslint-disable-next-line no-unused-vars
    const [chartSettings, _] = useChartSettings();
    const [graphData, setGraphData] = useState({
      data: [],
      centroidDataObject: [],
      peaks: [],
    });
    const {
      profile,
      centroid,
      fwhm,
      peaks,
      relative = false,
      shift = 0,
      xLabel = 'm/z',
    } = settings ? settings : chartSettings;

    useEffect(() => {
      if (option === {}) return;

      const profileData = [];
      const centroidData = [];
      let centroidDataObject = [];
      let profileDataObject = [];
      let data = [];
      let nonValideIonizations = false;

      try {
        const mzShift = relative
          ? -new MF(mf).getInfo().observedMonoisotopicMass
          : shift;
        const isotopicDistribution = new IsotopicDistribution(mf, {
          ensureCase: true,
          ...option,
          fwhm: fwhm / 10,
        });
        if (profile) {
          try {
            profileDataObject = isotopicDistribution.getGaussian({
              maxValue: 100,
              peakWidthFct: () => fwhm,
            });
            profileDataObject.x.forEach((x, i) => {
              const y = profileDataObject.y[i];
              profileData.push([x + mzShift, y]);
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log('Skipping profile spectrum, too many points');
          }
        }

        if (centroid) {
          centroidDataObject = isotopicDistribution.getTable({
            maxValue: 100,
          });
          centroidDataObject.forEach((entry) => (entry.x += mzShift));
          centroidDataObject.forEach((e) => centroidData.push([e.x, e.y]));
        }
      } catch (error) {
        nonValideIonizations = true;
      }

      profileData.forEach((e) => {
        data.push([e[0], e[1], null]);
      });

      centroidData.forEach((e) => {
        data.push([e[0], null, e[1]]);
      });

      if (data.length === 0) data.push([0, null, null]);

      if (nonValideIonizations === false) {
        data.unshift([data[0][0] - 3, profile ? 0 : null, centroid ? 0 : null]);
        data.push([
          data[data.length - 1][0] + 3,
          profile ? 0 : null,
          centroid ? 0 : null,
        ]);
      }

      setGraphData({ data, centroidDataObject, peaks });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mf, option, profile, centroid, fwhm, peaks]);

    useEffect(() => {
      const { data, centroidDataObject } = graphData;
      if (data.length === 0) return;
      const g = new DyGraph(document.getElementById(`graph_${id}`), data, {
        title: 'Isotopic distribution',
        labels: ['x', 'Profile', 'Centroid'],
        animatedZooms: true,
        includeZero: true,
        xlabel: xLabel,
        ylabel: 'Relative intensity (%)',
        valueRange: [0.0, 125.0],
        axes: {
          x: {
            drawGrid: true,
            axisLabelFontSize: 20,
            valueFormatter: function (x) {
              return ` m/z: ${x.toFixed(4)} `;
            },
          },
          y: {
            drawGrid: true,
            axisLabelFontSize: 20,
            valueFormatter: function (x) {
              return `${x.toFixed(0)} %`;
            },
          },
        },
        series: {
          Profile: {
            strokeWidth: 2,
          },
          Centroid: {
            plotter: barChartPlotter,
            color: 'red',
          },
        },
        zoomCallback: function () {
          if (peaks) bestPeakAnnotator(g, centroidDataObject);
        },

        interactionModel,
      });

      g.ready(() => {
        if (peaks) bestPeakAnnotator(g, centroidDataObject);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [style, graphData, peaks, id]);

    return (
      <div
        id={`graph_${id}`}
        className="flex justify-right overflow-x-scroll "
        style={style}
      />
    );
  },
);

export default IsotopicDistributionChart;
